import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  Building2,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  Download,
  FileText,
  Info,
  LayoutDashboard,
  Printer,
  Receipt,
  Smile,
  Sparkles,
  Stethoscope,
  Target,
  TrendingUp,
  Users,
  WalletCards,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const COLORS = ['#7C3AED', '#2563EB', '#0F766E', '#EA580C', '#DC2626', '#0891B2'];

const TABS = [
  { id: 'overview', label: 'Visão geral', icon: LayoutDashboard },
  { id: 'revenue', label: 'Receitas', icon: CircleDollarSign },
  { id: 'expenses', label: 'Despesas', icon: Receipt },
  { id: 'management', label: 'Gestão clínica', icon: Stethoscope },
  { id: 'forecast', label: 'Projeções', icon: CalendarClock },
];

const safeNumber = (value) => Number(value || 0);

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const percentage = (part, total) => {
  const denominator = safeNumber(total);
  if (denominator <= 0) return 0;
  return (safeNumber(part) / denominator) * 100;
};

const EmptyState = ({ title, description }) => (
  <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
    <BarChart3 className="mx-auto h-8 w-8 text-gray-400" />
    <h4 className="mt-3 text-sm font-semibold text-gray-800">{title}</h4>
    <p className="mx-auto mt-1 max-w-xl text-sm text-gray-500">{description}</p>
  </div>
);

const Section = ({ title, description, icon: Icon, children, className = '' }) => (
  <section className={`report-section rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6 ${className}`}>
    <div className="mb-5 flex items-start gap-3">
      {Icon && (
        <div className="rounded-xl bg-purple-50 p-2.5 text-purple-700">
          <Icon className="h-5 w-5" />
        </div>
      )}
      <div>
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
      </div>
    </div>
    {children}
  </section>
);

const KpiCard = ({ label, value, helper, icon: Icon, accent = 'bg-gray-50 text-gray-700', onClick }) => {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={`report-kpi w-full rounded-xl border border-gray-200 bg-white p-4 text-left shadow-sm transition ${
        onClick ? 'hover:-translate-y-0.5 hover:border-purple-300 hover:shadow-md' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 break-words text-2xl font-bold text-gray-900">{value}</p>
          {helper && <p className="mt-1 text-xs text-gray-500">{helper}</p>}
        </div>
        {Icon && (
          <span className={`shrink-0 rounded-xl p-2.5 ${accent}`}>
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
    </Wrapper>
  );
};

const ComparisonBadge = ({ value }) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return <span className="text-xs text-gray-500">Sem base anterior</span>;
  }
  const numericValue = Number(value);
  const positive = numericValue >= 0;
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-semibold ${positive ? 'text-green-700' : 'text-red-700'}`}>
      <Icon className="h-3.5 w-3.5" />
      {Math.abs(numericValue).toFixed(1)}%
    </span>
  );
};

const RankingTable = ({ rows, nameKey, nameLabel, formatCurrency, emptyDescription, valueKey = 'invoiced' }) => {
  if (!rows.length) {
    return <EmptyState title="Ainda não há dados para este ranking" description={emptyDescription} />;
  }
  const total = rows.reduce((sum, item) => sum + safeNumber(item[valueKey]), 0);
  const maxValue = Math.max(...rows.map((item) => safeNumber(item[valueKey])), 1);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[660px] text-sm">
        <thead className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="py-3 pr-3">#</th>
            <th className="py-3 pr-3">{nameLabel}</th>
            <th className="py-3 pr-3 text-right">Faturado</th>
            <th className="py-3 pr-3 text-right">Recebido</th>
            <th className="py-3 text-right">Participação</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((item, index) => {
            const share = percentage(item[valueKey], total);
            const barWidth = percentage(item[valueKey], maxValue);
            return (
              <tr key={`${item[nameKey] || 'item'}-${index}`}>
                <td className="py-3 pr-3 font-semibold text-gray-400">{index + 1}</td>
                <td className="py-3 pr-3">
                  <p className="font-medium text-gray-900">{item[nameKey] || 'Não informado'}</p>
                  <div className="mt-1 h-1.5 w-full max-w-[240px] overflow-hidden rounded-full bg-gray-100">
                    <div className="h-full rounded-full bg-purple-500" style={{ width: `${Math.max(barWidth, 2)}%` }} />
                  </div>
                </td>
                <td className="py-3 pr-3 text-right font-medium text-gray-900">{formatCurrency(item.invoiced)}</td>
                <td className="py-3 pr-3 text-right text-gray-600">{formatCurrency(item.received)}</td>
                <td className="py-3 text-right font-semibold text-purple-700">{share.toFixed(1)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const FinancialReportsPanel = ({ report, formatCurrency, onNavigate }) => {
  const [activeView, setActiveView] = useState('overview');
  const data = report || {};

  const monthly = Array.isArray(data.monthly_revenue) ? data.monthly_revenue : [];
  const sources = Array.isArray(data.revenue_sources)
    ? data.revenue_sources.filter((item) => safeNumber(item.value) > 0)
    : [];
  const partners = Array.isArray(data.partner_breakdown) ? data.partner_breakdown : [];
  const services = Array.isArray(data.service_breakdown) ? data.service_breakdown : [];
  const expenseCategories = Array.isArray(data.expense_category_breakdown) ? data.expense_category_breakdown : [];
  const insights = Array.isArray(data.insights) ? data.insights : [];
  const aligners = data.aligner_summary || {};
  const cashForecast = data.cash_forecast || {};
  const comparison = data.comparison || {};
  const projection = data.cash_projection || {};
  const executiveSummary = data.executive_summary || {};
  const executiveItems = Array.isArray(executiveSummary.items) ? executiveSummary.items : [];
  const management = data.management_summary || {};
  const procedureRanking = Array.isArray(management.procedure_ranking) ? management.procedure_ranking : [];
  const professionalRanking = Array.isArray(management.professional_ranking) ? management.professional_ranking : [];
  const patientRanking = Array.isArray(management.patient_ranking) ? management.patient_ranking : [];
  const concentration = management.concentration || {};

  const collectionRate = Math.max(0, Math.min(safeNumber(data.collection_rate), 100));
  const operatingResult = safeNumber(data.operating_result);
  const totalExpenses = safeNumber(data.total_expenses);
  const totalExpensesPaid = safeNumber(data.total_expenses_paid);
  const periodLabel = data.period_start && data.period_end
    ? `${formatDate(data.period_start)} a ${formatDate(data.period_end)}`
    : 'Período selecionado';

  const health = useMemo(() => {
    const pending = safeNumber(data.total_pending);
    const overdue = safeNumber(data.total_overdue);
    if (overdue > 0 && pending > 0 && overdue / pending >= 0.35) {
      return { label: 'Atenção necessária', description: 'Uma parcela relevante do saldo a receber já está vencida.', className: 'bg-red-100 text-red-800', bar: 'bg-red-500' };
    }
    if (collectionRate >= 90 && operatingResult >= 0) {
      return { label: 'Saudável', description: 'Boa conversão do faturamento em caixa e resultado operacional positivo.', className: 'bg-green-100 text-green-800', bar: 'bg-green-500' };
    }
    if (collectionRate >= 75 && operatingResult >= 0) {
      return { label: 'Estável', description: 'Os indicadores estão equilibrados, com pontos que ainda merecem acompanhamento.', className: 'bg-blue-100 text-blue-800', bar: 'bg-blue-500' };
    }
    return { label: 'Requer acompanhamento', description: 'Há diferença relevante entre faturamento, recebimentos e despesas.', className: 'bg-amber-100 text-amber-800', bar: 'bg-amber-500' };
  }, [collectionRate, data.total_overdue, data.total_pending, operatingResult]);

  const navigate = (action) => {
    if (action && onNavigate) onNavigate(action);
  };

  const exportCsv = () => {
    const rows = [];
    const addSection = (title, headers, values) => {
      rows.push([title]);
      rows.push(headers);
      values.forEach((row) => rows.push(row));
      rows.push([]);
    };

    addSection('Resumo financeiro', ['Indicador', 'Valor'], [
      ['Período', periodLabel],
      ['Faturado', safeNumber(data.total_invoiced)],
      ['Recebido', safeNumber(data.total_received)],
      ['A receber', safeNumber(data.total_pending)],
      ['Vencido', safeNumber(data.total_overdue)],
      ['Despesas', totalExpenses],
      ['Despesas pagas', totalExpensesPaid],
      ['Contas a pagar', safeNumber(data.total_accounts_payable)],
      ['Resultado operacional', operatingResult],
      ['Margem operacional (%)', safeNumber(data.operating_margin)],
      ['Taxa de recebimento (%)', collectionRate],
    ]);

    addSection('Receitas por fonte', ['Fonte', 'Valor'], sources.map((item) => [item.name, safeNumber(item.value)]));
    addSection('Receitas por parceiro', ['Parceiro', 'Faturado', 'Recebido', 'Em aberto'], partners.map((item) => [item.partner_name || item.name, safeNumber(item.invoiced || item.value), safeNumber(item.received), safeNumber(item.open_amount || item.pending)]));
    addSection('Receitas por serviço', ['Serviço', 'Faturado', 'Recebido'], services.map((item) => [item.service_name || item.name, safeNumber(item.invoiced || item.value), safeNumber(item.received)]));
    addSection('Despesas por categoria', ['Categoria', 'Total', 'Pago', 'Saldo', 'Lançamentos'], expenseCategories.map((item) => [item.name, safeNumber(item.value), safeNumber(item.paid), safeNumber(item.value) - safeNumber(item.paid), safeNumber(item.count)]));
    addSection('Ranking de procedimentos', ['Procedimento', 'Faturado', 'Recebido', 'Quantidade'], procedureRanking.map((item) => [item.procedure_name, safeNumber(item.invoiced), safeNumber(item.received), safeNumber(item.quantity)]));
    addSection('Ranking de profissionais', ['Profissional', 'Faturado', 'Recebido', 'Faturas'], professionalRanking.map((item) => [item.professional_name, safeNumber(item.invoiced), safeNumber(item.received), safeNumber(item.invoices)]));
    addSection('Ranking de pacientes', ['Paciente', 'Faturado', 'Recebido', 'Em aberto'], patientRanking.map((item) => [item.patient_name, safeNumber(item.invoiced), safeNumber(item.received), safeNumber(item.pending)]));

    const escape = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const csv = `\uFEFF${rows.map((row) => row.map(escape).join(';')).join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-financeiro-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {executiveItems.length > 0 && (
        <section className="report-section rounded-2xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50 p-5 shadow-sm sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex items-start gap-3">
              <div className="rounded-xl bg-purple-100 p-3"><Sparkles className="h-6 w-6 text-purple-700" /></div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-lg font-semibold text-gray-900">{executiveSummary.title || 'Resumo financeiro da clínica'}</h3>
                  {executiveSummary.status_label && <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-purple-700">{executiveSummary.status_label}</span>}
                </div>
                <p className="mt-1 text-sm text-gray-600">{executiveSummary.subtitle || 'Leitura automática dos principais números do período.'}</p>
              </div>
            </div>
            <span className="rounded-full border border-purple-200 bg-white px-3 py-1 text-xs font-medium text-purple-700">Análise automática · sem custo de IA</span>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-2">
            {executiveItems.map((item, index) => {
              const warning = item.type === 'warning';
              const positive = item.type === 'positive';
              const Icon = warning ? AlertTriangle : positive ? CheckCircle2 : Info;
              return (
                <div key={`${item.type || 'item'}-${index}`} className={`rounded-xl border p-4 ${warning ? 'border-amber-200 bg-amber-50' : positive ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-start gap-3"><Icon className="mt-0.5 h-5 w-5 shrink-0 text-gray-700" /><div><p className="font-semibold text-gray-900">{item.title}</p><p className="mt-1 text-sm text-gray-600">{item.description || item.text}</p></div></div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <KpiCard label="Recebido no período" value={formatCurrency(data.total_received)} helper="Valor efetivamente recebido" icon={CircleDollarSign} accent="bg-green-50 text-green-700" onClick={() => navigate({ target: 'payments', label: 'Recebimentos do período selecionado' })} />
        <KpiCard label="A receber" value={formatCurrency(data.total_pending)} helper="Saldo ainda pendente" icon={WalletCards} accent="bg-blue-50 text-blue-700" onClick={() => navigate({ target: 'invoices', filter: 'receivables', label: 'Faturas com saldo a receber' })} />
        <KpiCard label="Vencido" value={formatCurrency(data.total_overdue)} helper={safeNumber(data.total_overdue) > 0 ? 'Priorize a cobrança' : 'Nenhum valor vencido'} icon={AlertTriangle} accent={safeNumber(data.total_overdue) > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} onClick={() => navigate({ target: 'invoices', filter: 'overdue', label: 'Faturas vencidas' })} />
        <KpiCard label="Despesas pagas" value={formatCurrency(totalExpensesPaid)} helper="Saídas efetivamente pagas" icon={ArrowDownRight} accent="bg-red-50 text-red-700" onClick={() => navigate({ target: 'expenses', label: 'Despesas do período' })} />
        <KpiCard label="Resultado operacional" value={formatCurrency(operatingResult)} helper={`${safeNumber(data.operating_margin).toFixed(1)}% de margem`} icon={TrendingUp} accent={operatingResult >= 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'} />
        <KpiCard label="Projeção de fechamento" value={formatCurrency(projection.projected_month_revenue)} helper="Estimativa pelo ritmo do mês" icon={Target} accent="bg-purple-50 text-purple-700" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section title="Composição do resultado" description="Como os recebimentos se transformaram no resultado operacional." icon={CircleDollarSign}>
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between rounded-lg bg-green-50 px-4 py-3"><span className="text-gray-700">Recebimentos</span><strong className="text-green-800">{formatCurrency(data.total_received)}</strong></div>
            <div className="flex items-center justify-between rounded-lg bg-red-50 px-4 py-3"><span className="text-gray-700">(-) Despesas pagas</span><strong className="text-red-800">{formatCurrency(totalExpensesPaid)}</strong></div>
            <div className="border-t border-dashed pt-3">
              <div className="flex items-center justify-between"><span className="font-semibold text-gray-900">Resultado operacional</span><strong className={operatingResult >= 0 ? 'text-emerald-700' : 'text-red-700'}>{formatCurrency(operatingResult)}</strong></div>
              <div className="mt-1 flex items-center justify-between text-xs text-gray-500"><span>Margem operacional</span><span>{safeNumber(data.operating_margin).toFixed(1)}%</span></div>
            </div>
          </div>
        </Section>

        <Section title="Situação financeira" description={health.description} icon={TrendingUp}>
          <div className="flex items-center justify-between gap-3"><span className={`rounded-full px-3 py-1 text-sm font-semibold ${health.className}`}>{health.label}</span><strong className="text-2xl text-gray-900">{collectionRate.toFixed(1)}%</strong></div>
          <div className="mt-4 h-2.5 overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${health.bar}`} style={{ width: `${collectionRate}%` }} /></div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm"><div className="rounded-lg bg-gray-50 p-3"><p className="text-gray-500">Faturado</p><p className="mt-1 font-semibold text-gray-900">{formatCurrency(data.total_invoiced)}</p></div><div className="rounded-lg bg-gray-50 p-3"><p className="text-gray-500">Ticket médio</p><p className="mt-1 font-semibold text-gray-900">{formatCurrency(data.average_ticket)}</p></div></div>
        </Section>
      </div>

      {insights.length > 0 && (
        <Section title="Prioridades do período" description="Pontos que merecem ação ou acompanhamento." icon={AlertTriangle}>
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            {insights.map((item, index) => {
              const warning = item.type === 'warning';
              const positive = item.type === 'positive';
              const Icon = warning ? AlertTriangle : positive ? CheckCircle2 : Info;
              return <div key={`${item.title}-${index}`} className={`rounded-xl border p-4 ${warning ? 'border-amber-200 bg-amber-50' : positive ? 'border-green-200 bg-green-50' : 'border-blue-200 bg-blue-50'}`}><div className="flex items-start gap-3"><Icon className="mt-0.5 h-5 w-5 shrink-0" /><div><h4 className="font-semibold">{item.title}</h4><p className="mt-1 text-sm">{item.description}</p></div></div></div>;
            })}
          </div>
        </Section>
      )}

      {Object.keys(comparison).length > 0 && (
        <Section title="Comparação com o período anterior" description="Variações calculadas sobre um intervalo equivalente." icon={TrendingUp}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ['Faturado', comparison.invoiced_change_percentage],
              ['Recebido', comparison.received_change_percentage],
              ['Ticket médio', comparison.average_ticket_change_percentage],
              ['Taxa de recebimento', comparison.collection_rate_change_percentage],
            ].map(([label, value]) => <div key={label} className="rounded-lg border border-gray-100 bg-gray-50 p-4"><p className="text-sm text-gray-600">{label}</p><div className="mt-2"><ComparisonBadge value={value} /></div></div>)}
          </div>
        </Section>
      )}
    </div>
  );

  const renderRevenue = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Faturado" value={formatCurrency(data.total_invoiced)} icon={FileText} onClick={() => navigate({ target: 'invoices', filter: 'all', label: 'Todas as faturas do período' })} />
        <KpiCard label="Recebido" value={formatCurrency(data.total_received)} icon={CircleDollarSign} accent="bg-green-50 text-green-700" onClick={() => navigate({ target: 'payments', label: 'Recebimentos do período' })} />
        <KpiCard label="Receitas de pacientes" value={formatCurrency(data.patient_revenue)} icon={Users} accent="bg-blue-50 text-blue-700" />
        <KpiCard label="Receitas externas" value={formatCurrency(data.external_revenue)} icon={Building2} accent="bg-purple-50 text-purple-700" />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section title="Evolução financeira" description="Faturamento e recebimentos ao longo dos meses." icon={TrendingUp}>
          {monthly.length > 0 ? <div className="h-80"><ResponsiveContainer width="100%" height="100%"><LineChart data={monthly}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="month" /><YAxis /><Tooltip formatter={(value) => formatCurrency(value)} /><Legend /><Line type="monotone" dataKey="invoiced" name="Faturado" stroke="#7C3AED" strokeWidth={2} /><Line type="monotone" dataKey="received" name="Recebido" stroke="#0F766E" strokeWidth={2} /></LineChart></ResponsiveContainer></div> : <EmptyState title="Sem evolução financeira" description="O gráfico será exibido quando houver faturamento registrado." />}
        </Section>

        <Section title="Origem da receita" description="Participação de pacientes e receitas externas." icon={CircleDollarSign}>
          {sources.length > 0 ? <div className="h-80"><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={sources} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>{sources.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}</Pie><Tooltip formatter={(value) => formatCurrency(value)} /></PieChart></ResponsiveContainer></div> : <EmptyState title="Nenhuma receita registrada" description="A composição será exibida quando houver receitas de pacientes ou parceiros." />}
        </Section>
      </div>

      <Section title="Receita por parceiro" description="Receitas externas organizadas por parceiro comercial." icon={Building2}>
        {partners.length > 0 ? <div className="overflow-x-auto"><table className="w-full min-w-[680px] text-sm"><thead className="border-b text-left text-xs uppercase text-gray-500"><tr><th className="py-3">Parceiro</th><th className="text-right">Faturado</th><th className="text-right">Recebido</th><th className="text-right">Em aberto</th><th className="text-right">Participação</th></tr></thead><tbody className="divide-y">{partners.map((item) => <tr key={item.partner_id || item.partner_name}><td className="py-3"><button type="button" onClick={() => navigate({ target: 'invoices', filter: 'partner', value: item.partner_id || item.partner_name, label: `Receitas de ${item.partner_name || item.name}` })} className="font-medium text-purple-700 hover:underline">{item.partner_name || item.name}</button></td><td className="text-right">{formatCurrency(item.invoiced || item.value)}</td><td className="text-right">{formatCurrency(item.received)}</td><td className="text-right">{formatCurrency(item.open_amount || item.pending)}</td><td className="text-right font-semibold">{safeNumber(item.share_percentage).toFixed(1)}%</td></tr>)}</tbody></table></div> : <EmptyState title="Nenhuma receita de parceiro" description="As receitas externas aparecerão aqui quando forem vinculadas a parceiros." />}
      </Section>

      <Section title="Receita por serviço" description="Serviços que mais contribuíram para o faturamento externo." icon={FileText}>
        {services.length > 0 ? <div className="overflow-x-auto"><table className="w-full min-w-[620px] text-sm"><thead className="border-b text-left text-xs uppercase text-gray-500"><tr><th className="py-3">Serviço</th><th className="text-right">Faturado</th><th className="text-right">Recebido</th><th className="text-right">Registros</th></tr></thead><tbody className="divide-y">{services.map((item) => <tr key={item.service_category || item.name}><td className="py-3"><button type="button" onClick={() => navigate({ target: 'invoices', filter: 'service', value: item.service_category || item.name, label: `Receitas de ${item.service_name || item.name}` })} className="font-medium text-purple-700 hover:underline">{item.service_name || item.name}</button></td><td className="text-right">{formatCurrency(item.invoiced || item.value)}</td><td className="text-right">{formatCurrency(item.received)}</td><td className="text-right">{item.count || 0}</td></tr>)}</tbody></table></div> : <EmptyState title="Nenhum serviço identificado" description="O detalhamento será exibido quando houver receitas externas classificadas." />}
      </Section>

      <Section title="Métodos de recebimento" description="Valores recebidos e quantidade de transações por método." icon={WalletCards}>
        {data.payment_methods_breakdown && Object.keys(data.payment_methods_breakdown).length > 0 ? <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{Object.entries(data.payment_methods_breakdown).map(([method, item]) => <div key={method} className="rounded-xl border border-gray-100 bg-gray-50 p-4"><p className="text-sm font-medium text-gray-700">{method}</p><p className="mt-1 text-xl font-bold text-gray-900">{formatCurrency(item.total)}</p><p className="mt-1 text-xs text-gray-500">{item.count} transação(ões)</p></div>)}</div> : <EmptyState title="Nenhum recebimento registrado" description="Os métodos utilizados aparecerão após o registro de recebimentos." />}
      </Section>
    </div>
  );

  const renderExpenses = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Despesas lançadas" value={formatCurrency(totalExpenses)} icon={Receipt} accent="bg-red-50 text-red-700" onClick={() => navigate({ target: 'expenses', label: 'Despesas do período' })} />
        <KpiCard label="Despesas pagas" value={formatCurrency(totalExpensesPaid)} icon={CheckCircle2} accent="bg-green-50 text-green-700" />
        <KpiCard label="Contas a pagar" value={formatCurrency(data.total_accounts_payable)} icon={WalletCards} accent="bg-blue-50 text-blue-700" />
        <KpiCard label="Despesas vencidas" value={formatCurrency(data.total_expenses_overdue)} icon={AlertTriangle} accent={safeNumber(data.total_expenses_overdue) > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Section title="Despesas por categoria" description="Distribuição das despesas lançadas no período." icon={BarChart3}>
          {expenseCategories.length > 0 ? <div className="h-80"><ResponsiveContainer width="100%" height="100%"><BarChart data={expenseCategories.slice(0, 10)} layout="vertical" margin={{ left: 25 }}><CartesianGrid strokeDasharray="3 3" /><XAxis type="number" /><YAxis dataKey="name" type="category" width={105} tick={{ fontSize: 11 }} /><Tooltip formatter={(value) => formatCurrency(value)} /><Bar dataKey="value" name="Total" fill="#DC2626" radius={[0, 4, 4, 0]} /></BarChart></ResponsiveContainer></div> : <EmptyState title="Sem despesas classificadas" description="Cadastre despesas e categorias para visualizar a composição dos gastos." />}
        </Section>

        <Section title="Situação das despesas" description="Comparação entre o total lançado, pago e ainda pendente." icon={Receipt}>
          <div className="space-y-4">
            {[
              ['Total lançado', totalExpenses, 'bg-gray-500'],
              ['Pago', totalExpensesPaid, 'bg-green-500'],
              ['Saldo pendente', Math.max(totalExpenses - totalExpensesPaid, 0), 'bg-amber-500'],
            ].map(([label, value, color]) => <div key={label}><div className="mb-1 flex items-center justify-between text-sm"><span className="text-gray-600">{label}</span><strong className="text-gray-900">{formatCurrency(value)}</strong></div><div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${color}`} style={{ width: `${Math.min(percentage(value, totalExpenses), 100)}%` }} /></div></div>)}
          </div>
        </Section>
      </div>

      <Section title="Detalhamento por categoria" description="Total, valor pago, saldo e participação de cada categoria." icon={Receipt}>
        {expenseCategories.length > 0 ? <div className="overflow-x-auto"><table className="w-full min-w-[760px] text-sm"><thead className="border-b text-left text-xs uppercase text-gray-500"><tr><th className="py-3">Categoria</th><th className="text-right">Total</th><th className="text-right">Pago</th><th className="text-right">Saldo</th><th className="text-right">Participação</th><th className="text-right">Lançamentos</th></tr></thead><tbody className="divide-y">{expenseCategories.map((item) => { const balance = Math.max(safeNumber(item.value) - safeNumber(item.paid), 0); return <tr key={item.category_id || item.name}><td className="py-3 font-medium text-gray-900">{item.name || 'Sem categoria'}</td><td className="text-right">{formatCurrency(item.value)}</td><td className="text-right text-green-700">{formatCurrency(item.paid)}</td><td className="text-right text-amber-700">{formatCurrency(balance)}</td><td className="text-right font-semibold">{percentage(item.value, totalExpenses).toFixed(1)}%</td><td className="text-right">{item.count || 0}</td></tr>; })}</tbody></table></div> : <EmptyState title="Nenhuma categoria com movimento" description="O detalhamento será exibido quando houver despesas no período selecionado." />}
      </Section>
    </div>
  );

  const renderManagement = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Pacientes faturados" value={safeNumber(concentration.patients_billed).toLocaleString('pt-BR')} helper="Pacientes com faturamento no período" icon={Users} />
        <KpiCard label="Pacientes recorrentes" value={safeNumber(concentration.patients_with_repeated_billing).toLocaleString('pt-BR')} helper="Com duas ou mais cobranças" icon={Users} accent="bg-blue-50 text-blue-700" />
        <KpiCard label="Maior paciente" value={`${safeNumber(concentration.top_patient_share_percentage).toFixed(1)}%`} helper="Participação no faturamento" icon={Target} accent="bg-purple-50 text-purple-700" />
        <KpiCard label="Top 5 pacientes" value={`${safeNumber(concentration.top_5_patients_share_percentage).toFixed(1)}%`} helper="Concentração do faturamento" icon={TrendingUp} accent="bg-amber-50 text-amber-700" />
      </div>

      <Section title="Ranking de procedimentos" description="Procedimentos que mais contribuíram para o faturamento." icon={Stethoscope}>
        <RankingTable rows={procedureRanking} nameKey="procedure_name" nameLabel="Procedimento" formatCurrency={formatCurrency} emptyDescription="Vincule procedimentos às faturas para gerar este ranking." />
      </Section>

      <Section title="Ranking de profissionais" description="Produção financeira organizada por profissional." icon={Users}>
        <RankingTable rows={professionalRanking} nameKey="professional_name" nameLabel="Profissional" formatCurrency={formatCurrency} emptyDescription="Vincule profissionais aos atendimentos faturados para gerar este ranking." />
      </Section>

      <Section title="Ranking de pacientes" description="Pacientes com maior faturamento no período." icon={Users}>
        <RankingTable rows={patientRanking} nameKey="patient_name" nameLabel="Paciente" formatCurrency={formatCurrency} emptyDescription="O ranking será exibido quando houver faturas vinculadas a pacientes." />
      </Section>

      <Section title="Indicadores de alinhadores" description="Casos e receitas vinculados a tratamentos com alinhadores." icon={Smile}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ['Casos', aligners.total_cases || aligners.cases],
            ['Em andamento', aligners.in_progress],
            ['Concluídos', aligners.completed],
            ['Receita vinculada', formatCurrency(aligners.total_revenue || aligners.revenue)],
          ].map(([label, value]) => <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 p-4"><p className="text-sm text-gray-600">{label}</p><p className="mt-1 text-xl font-bold text-gray-900">{value || 0}</p></div>)}
        </div>
        {Array.isArray(aligners.by_provider) && aligners.by_provider.length > 0 && <div className="mt-5 overflow-x-auto"><table className="w-full min-w-[620px] text-sm"><thead className="border-b text-left text-xs uppercase text-gray-500"><tr><th className="py-3">Parceiro</th><th className="text-right">Casos</th><th className="text-right">Em andamento</th><th className="text-right">Concluídos</th><th className="text-right">Receita</th></tr></thead><tbody className="divide-y">{aligners.by_provider.map((item) => <tr key={item.provider}><td className="py-3 font-medium text-gray-900">{item.provider}</td><td className="text-right">{item.cases}</td><td className="text-right">{item.in_progress}</td><td className="text-right">{item.completed}</td><td className="text-right">{formatCurrency(item.revenue)}</td></tr>)}</tbody></table></div>}
      </Section>
    </div>
  );

  const renderForecast = () => {
    const forecastCards = [
      ['Vencidos e hoje', cashForecast.overdue_and_today],
      ['Próximos 7 dias', cashForecast.next_7_days],
      ['Próximos 30 dias', cashForecast.next_30_days],
      ['Próximos 90 dias', cashForecast.next_90_days],
    ];
    return (
      <div className="space-y-6">
        <Section title="Previsão de recebimentos" description="Valores previstos conforme os vencimentos das faturas e receitas externas." icon={CalendarClock}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">{forecastCards.map(([label, value]) => <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 p-4"><p className="text-sm text-gray-600">{label}</p><p className="mt-1 text-xl font-bold text-gray-900">{formatCurrency(value)}</p></div>)}</div>
        </Section>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <KpiCard label="Projeção de fechamento" value={formatCurrency(projection.projected_month_revenue)} helper="Estimativa de receita do mês" icon={Target} accent="bg-purple-50 text-purple-700" />
          <KpiCard label="A receber em 30 dias" value={formatCurrency(cashForecast.next_30_days)} icon={ArrowUpRight} accent="bg-green-50 text-green-700" />
          <KpiCard label="Contas a pagar" value={formatCurrency(data.total_accounts_payable)} icon={ArrowDownRight} accent="bg-red-50 text-red-700" />
          <KpiCard label="Saldo projetado" value={formatCurrency(data.projected_cash_balance)} icon={WalletCards} accent={safeNumber(data.projected_cash_balance) >= 0 ? 'bg-blue-50 text-blue-700' : 'bg-red-50 text-red-700'} />
        </div>

        <Section title="Projeção financeira" description="Estimativas construídas a partir do ritmo do período atual." icon={TrendingUp}>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {[
              ['Receita atual', projection.current_revenue],
              ['Projeção do mês', projection.projected_month_revenue],
              ['Média diária', projection.daily_average],
              ['Dias considerados', projection.elapsed_days],
            ].map(([label, value]) => <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 p-4"><p className="text-sm text-gray-600">{label}</p><p className="mt-1 text-xl font-bold text-gray-900">{label === 'Dias considerados' ? safeNumber(value).toLocaleString('pt-BR') : formatCurrency(value)}</p></div>)}
          </div>
        </Section>

        <Section title="Leitura do caixa futuro" description="Relação entre entradas previstas e compromissos financeiros." icon={WalletCards}>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between rounded-lg bg-green-50 px-4 py-3"><span>Entradas previstas em 30 dias</span><strong className="text-green-800">{formatCurrency(cashForecast.next_30_days)}</strong></div>
            <div className="flex justify-between rounded-lg bg-red-50 px-4 py-3"><span>(-) Contas a pagar</span><strong className="text-red-800">{formatCurrency(data.total_accounts_payable)}</strong></div>
            <div className="flex justify-between border-t border-dashed pt-3"><span className="font-semibold">Saldo projetado</span><strong className={safeNumber(data.projected_cash_balance) >= 0 ? 'text-blue-700' : 'text-red-700'}>{formatCurrency(data.projected_cash_balance)}</strong></div>
          </div>
        </Section>
      </div>
    );
  };

  return (
    <div id="financial-report-print-area" className="space-y-6">
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #financial-report-print-area, #financial-report-print-area * { visibility: visible !important; }
          #financial-report-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 0; }
          .report-no-print { display: none !important; }
          .report-section, .report-kpi { break-inside: avoid; box-shadow: none !important; }
          .report-print-all { display: block !important; }
          .report-print-header { display: block !important; }
        }
        @media screen {
          .report-print-header { display: none; }
        }
      `}</style>

      <div className="report-print-header mb-6 border-b pb-4">
        <h1 className="text-2xl font-bold">Relatório financeiro</h1>
        <p>Período analisado: {periodLabel}</p>
        <p className="text-sm text-gray-500">Gerado em {new Date().toLocaleString('pt-BR')}</p>
      </div>

      <header className="report-no-print flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Relatórios financeiros</h2>
          <p className="mt-1 text-sm text-gray-600">Período analisado: <strong>{periodLabel}</strong></p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"><Download className="h-4 w-4" />Exportar CSV</button>
          <button type="button" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700"><Printer className="h-4 w-4" />Imprimir relatório</button>
        </div>
      </header>

      <nav className="report-no-print overflow-x-auto rounded-xl border border-gray-200 bg-white p-1.5 shadow-sm">
        <div className="flex min-w-max gap-1">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeView === tab.id;
            return <button key={tab.id} type="button" onClick={() => setActiveView(tab.id)} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition ${active ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'}`}><Icon className="h-4 w-4" />{tab.label}</button>;
          })}
        </div>
      </nav>

      {activeView === 'overview' && renderOverview()}
      {activeView === 'revenue' && renderRevenue()}
      {activeView === 'expenses' && renderExpenses()}
      {activeView === 'management' && renderManagement()}
      {activeView === 'forecast' && renderForecast()}
    </div>
  );
};

export default FinancialReportsPanel;
